import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
// import Modal from "@material-ui/core/Modal";
import Save from "@material-ui/icons/Save";
// import Info from "@material-ui/icons/Info";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLoaderData, useNavigate, defer, useAsyncError } from "react-router-dom";
import { update } from "src/api/auth";
import { getAllWithAnswers } from "src/api/questions";
import { Details } from "src/components/campaigns/Details";
import { ContextProvider } from "src/components/campaigns/Details/Context";
import { Async } from "src/components/commons/Async";
import { Main } from "src/components/commons/Main";
import Alert from "src/helpers/alert";
import { clone } from "src/helpers/clone";
import { ONBOARDING_STEPS, QUESTION_CATEGORIES, QUESTION_TYPES, ROLES } from "src/helpers/constants";
import getByPath from "src/helpers/getByPath";
import { setAuthUser } from "src/store/slices/auth";
import { localstorage } from "src/lib/storage";
import { uploadAndProcessDocument, getUploadUrlOnly, uploadToS3, processFromS3 } from "src/api/documents";

const questionCategoryMap = {
	[ROLES.FUNDER]: QUESTION_CATEGORIES.FUNDER_PROFILE,
	[ROLES.FOUNDER]: QUESTION_CATEGORIES.FOUNDER_PROFILE,
	[ROLES.VENDOR]: QUESTION_CATEGORIES.VENDOR_PROFILE,
};

export default function Profile() {
	const navigate = useNavigate();
	const { authUser, currentRole } = useSelector(({ auth }) => auth);
	const dispatch = useDispatch();
	const [user] = useState(clone(authUser));
	const { promise } = useLoaderData();
	const error = useAsyncError();
	const storageKey = `${questionCategoryMap[currentRole].toLowerCase()}_${user.id}`;
	
		// PDF upload state
		const [selectedPdfFile, setSelectedPdfFile] = useState(null);
		const [pdfFileName, setPdfFileName] = useState("");
		const [isPdfProcessing, setIsPdfProcessing] = useState(false);
		const [showNotification, setShowNotification] = useState(false);
		const [notificationMessage, setNotificationMessage] = useState("");
		const [notificationSeverity, setNotificationSeverity] = useState("success");
	
	const prepareUserAnswers = useCallback((questions, user) => {
		return questions
			.map((question) => {
				const { field = "" } = question.meta || {};
				if (!field) {
					return [];
				}
				let value = getByPath(user, field?.split("."));
				const userAnswer = {
					questionId: question.id,
				};
				if (question.answers.length && question.type === QUESTION_TYPES.MULTIPLE_CHOICE) {
					const userAnswers = [];
					value = Array.isArray(value) ? value : [value];
					value.forEach((val) => {
						const answerId = question.answers.find((a) => val === a.answer)?.id;
						if (answerId) {
							userAnswers.push({
								questionId: question.id,
								answerId,
							});
						} else {
							userAnswers.push({
								questionId: question.id,
								response: val,
							});
						}
					});
					return userAnswers;
				} else if (question.answers.length && question.type !== QUESTION_TYPES.AUTOCOMPLETE) {
					userAnswer.answerId = question.answers.find((a) => a.answer === value)?.id;
				} else {
					if (field === "name") {
						value = JSON.stringify({
							firstname: user.firstName,
							lastname: user.lastName,
							title: user.profile.title,
							suffix: user.profile.suffix,
							middlename: user.profile.middlename,
						});
					} else if (field === "profile.workEmail") {
						value = user.email;
					}
					userAnswer.response = value;
				}
				return userAnswer;
			})
			.flat();
	}, []);

			// PDF upload handler - New Direct S3 Upload Flow
	const handlePdfUpload = useCallback(async (event) => {
		const file = event.target.files[0];
		if (!file) return;

		const showError = (message) => {
			setNotificationMessage(message);
			setNotificationSeverity('error');
			setShowNotification(true);
		};

		// Check file type (both MIME type and file extension)
		const isPdfMimeType = file.type === 'application/pdf';
		const isPdfExtension = file.name.toLowerCase().endsWith('.pdf');
		
		if (!isPdfMimeType && !isPdfExtension) {
			showError('Please select a PDF file only. Other file formats are not supported.');
			return;
		}

		// Check file size (3MB = 3 * 1024 * 1024 bytes)
		const maxSize = 3 * 1024 * 1024;
		if (file.size > maxSize) {
			showError('File size must be less than 3MB');
			return;
		}

		setSelectedPdfFile(file);
		setPdfFileName(file.name);
		setIsPdfProcessing(true);

		try {
			// Step 1: Get presigned URL for direct S3 upload
			console.log('Getting upload URL for:', file.name);
			const urlResponse = await getUploadUrlOnly(file.name);
			
			// Step 2: Upload directly to S3
			console.log('Uploading to S3:');
			await uploadToS3(urlResponse.uploadUrl, file);
			console.log('S3 upload completed');

			// Step 3: Process document from S3
			console.log('Processing document from S3:', urlResponse.s3Path);
			const response = await processFromS3({
				s3Path: urlResponse.s3Path,
				confidenceThreshold: 0.7,
				useEnhanced: true,
				enableCaching: true
			});

			const processingId = response?.metadata?.processingId || 'N/A';
			// setNotificationMessage(`Successfully processed document with ID: ${processingId}`);
			setNotificationMessage(`Successfully processed document`);
			setNotificationSeverity('success');

		} catch (err) {
			console.error("Error during document processing:", err);
			const errorMessage = err?.message || "An unknown error occurred.";
			showError(`Processing failed: ${errorMessage}`);
		} finally {
			setIsPdfProcessing(false);
			setShowNotification(true);
		}
	}, []);

	// Notification handler
	const handleCloseNotification = useCallback(() => {
		setShowNotification(false);
	}, []);

	const save = useCallback(
		async (userAnswers, answerObject, id2questionMap) => {
			const { completedStep, isCompleted } = authUser.onboarding[currentRole];
			console.log('Debug navigation condition:', {
				currentRole,
				completedStep,
				isCompleted,
				isFounder: currentRole === ROLES.FOUNDER,
				isSignupStep: completedStep === ONBOARDING_STEPS.SIGNUP,
				onboarding: authUser.onboarding
			});
			try {
				// Handle undefined or missing name field
				let name;
				try {
					name = answerObject.name ? JSON.parse(answerObject.name) : {};
				} catch (parseError) {
					console.error('Error parsing name field:', parseError);
					name = {};
				}
				
				// Provide default values if name fields are missing
				const payload = {
					id: user.id,
					firstName: name.firstname,
					lastName: name.lastname,
					email: user.email,
					profile: answerObject.profile,
				};
				// check if angel
				const positionQuestion = Array.from(id2questionMap.values()).find((q) => q.meta.field === "profile.positions");
				if (positionQuestion) {
					const positionQuestionUAIds = userAnswers.reduce((acc, ua) => {
						if (ua.questionId === positionQuestion.id) {
							acc.push(ua.answerId);
						}
						return acc;
					}, []);
					const positionKeywords = positionQuestion.answers.reduce((acc, answer) => {
						if (positionQuestionUAIds.includes(answer.id)) {
							acc.add(answer.meta.keyword);
						}
						return acc;
					}, new Set());
					// if more than one, means they invest as group, firm or angel
					// if only one and it is angel, then the user invests as angle only and so mark him as angel
					if (positionKeywords.size === 1 && positionKeywords.has("ANGEL")) {
						payload.profile.isAngel = true;
					}
				}
				payload.profile.middlename = name.middlename || "";
				payload.profile.suffix = name.suffix || "";
				payload.profile.title = name.title || "";
				
				// Update onboarding for ALL roles (FOUNDER, FUNDER, VENDOR) when not completed
				if (isCompleted === false) {
					payload.onboarding = { ...authUser.onboarding };
					payload.onboarding[currentRole] = {
						isCompleted: false,
						completedStep: payload.profile.isAngel ? ONBOARDING_STEPS.ORGANIZATION : ONBOARDING_STEPS.PROFILE,
					};
				}
				
				const _user = await update(payload);
				dispatch(setAuthUser(_user));
				localstorage.removeItem(storageKey);
				
				// Navigate to next step based on role and onboarding status
				if (isCompleted === false) {
					// If at SIGNUP step, move to profile completion page
					if (Number(completedStep) === ONBOARDING_STEPS.SIGNUP) {
						if (currentRole === ROLES.FOUNDER) {
							navigate("/founder-profile");
							return;
						}
						
						// FUNDER and VENDOR continue to their respective flows
					}
					if (Number(completedStep) === ONBOARDING_STEPS.PROFILE) {
						if (currentRole === ROLES.FOUNDER) {
							navigate("/dashboard");
							return;
						}
					}
					
					// After PROFILE step, determine next step
					const nextStep = payload.profile.isAngel ? ONBOARDING_STEPS.ORGANIZATION : ONBOARDING_STEPS.PROFILE;
					
					// Navigate based on role and next step
					if (currentRole === ROLES.FUNDER) {
						if (nextStep === ONBOARDING_STEPS.ORGANIZATION) {
							navigate("/funder-organization");
							return;
						}
					} else if (currentRole === ROLES.VENDOR) {
						if (nextStep === ONBOARDING_STEPS.SERVICE) {
							navigate("/vendor-services");
							return;
						}
					}
				}
				
				// Default: go to dashboard if onboarding is complete or no specific next step
				navigate("/dashboard");
				return;
			} catch (err) {
				Alert.error("Unable to save");
				console.error("Error while saving profile", err);
			}
		},
		[user]
	);
	if (error) {
		console.error("Error while fetching data", error);
	}

	return (
		<Main>
			<Async
				error={
					"Oops.. there was an error. Refresh the page and see if that fixes it. If the issue persists, please contact us"
				}
				promise={promise}
			>
				{({ questions }) => {
					return (
						<ContextProvider
							userAnswers={localstorage.getItem(storageKey) ? [] : prepareUserAnswers(questions, user)}
							questions={questions}
							saveFn={save}
							category={questionCategoryMap[currentRole]}
							entityId={user.id}
						>
							{({ validateAndSave, isSaving }) => {
								return (
									<>
										<Box
											display={"flex"}
											justifyContent="space-between"
											alignItems={"center"}
											p={4}
											px={4}
											borderBottom={"1px solid rgb(239 232 251)"}
											className="sticky top-[64px] bg-white rounded-t-lg z-10"
										>
											<Typography component={"h2"} variant="h6">
												My Profile
											</Typography>
											<Button
												form="profile"
												variant="contained"
												color="primary"
												startIcon={<Save />}
												type="submit"
												disabled={isSaving}
											>
												{isSaving ? "Saving..." : "Next"}
											</Button>
										</Box>
										{/* PDF Upload Section */}
										<Box display={"flex"} flexDirection="column" alignItems="center" margin={3}>
											<Typography variant="h6" gutterBottom>
											Upload Your Pitch Deck: If you upload your pitch deck, it will allow InvestorMatch.ai to auto-fill many questions for you. <p className="text-red-500">This is optional however this will save you time and effort.</p>
											</Typography>
											<Box display={"flex"} flexDirection="column" alignItems="center" gap={2}>
												<Button
													variant="outlined"
													color="primary"
													component="label"
													style={{ marginBottom: '10px' }}
													disabled={isPdfProcessing}
												>
													{isPdfProcessing ? (
														<Box display="flex" alignItems="center" gap={1}>
															<CircularProgress size={20} />
															Processing...
														</Box>
													) : (
														"Select PDF File (Max 3MB)"
													)}
													<input
														type="file"
														accept=".pdf,application/pdf"
														style={{ display: 'none' }}
														onChange={handlePdfUpload}
														disabled={isPdfProcessing}
													/>
												</Button>
												{pdfFileName && (
													<Typography variant="body2" color="textSecondary">
														Selected: {pdfFileName}
													</Typography>
												)}
												{isPdfProcessing && (
													<Typography variant="body2" color="primary">
														Processing pitch deck...
													</Typography>
												)}
											</Box>
										</Box>
										<Box
											component={"form"}
											p={4}
											px={4}
											height={"100%"}
											display="flex"
											flexDirection="column"
											id="profile"
											onSubmit={(e) => {
												e.preventDefault();
												validateAndSave();
											}}
										>
											<Details
												saveBtn={
													<Button
														key={"save-btn"}
														color="primary"
														variant="contained"
														startIcon={<Save />}
														type="submit"
														disabled={isSaving}
													>
														{isSaving ? "Saving..." : "Next"}
													</Button>
												}
											/>
											
											
											
											<Box display={"flex"} justifyContent="center" margin={5}>
												<Button
													key={"save-btn"}
													color="primary"
													variant="contained"
													startIcon={<Save />}
													type="submit"
													disabled={isSaving}
												>
													{isSaving ? "Saving..." : "Next"}
												</Button>
											</Box>
										</Box>
									</>
								);
							}}
						</ContextProvider>
					);
				}}
			</Async>
			
			{/* Notification Snackbar */}
			<Snackbar
				open={showNotification}
				autoHideDuration={6000}
				onClose={handleCloseNotification}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<MuiAlert 
					onClose={handleCloseNotification} 
					severity={notificationSeverity} 
					elevation={6} 
					variant="filled"
				>
					{notificationMessage}
				</MuiAlert>
			</Snackbar>
			
			{/* <Modal
				open={showIntroPopup}
				// maxWidth="xs"
				onClose={() => setShowIntroPopup(false)}
				aria-labelledby="intro-title"
				aria-describedby="intro-description"
			>
				<Box
					maxWidth={"100%"}
					width="400px"
					position={"absolute"}
					bgcolor="background.paper"
					borderRadius={12}
					padding={4}
					fontSize={16}
					className={"site-modal"}
				>
					<Box component={"h2"} textAlign="center" mb={2} id="intro-title">
						Welcome Aboard {authUser.firstName}!
					</Box>
					<Box component={"p"} className="" textAlign="center" mb={2} id="intro-description">
						Thank you for signing up. Let's begin with your profile.
					</Box>
					<Box display={"flex"} justifyContent="center" marginTop={4}>
						<Button type="button" variant="contained" color="primary" onClick={() => setShowIntroPopup(false)}>
							Let's begin
						</Button>
					</Box>
				</Box>
			</Modal> */}
		</Main>
	);
}

export function profileLoader({ auth }) {
	const category = questionCategoryMap[auth.currentRole];
	const promises = [getAllWithAnswers({ category: category.toUpperCase(), per_page: 100 })];
	// if (auth.currentRole === ROLES.FOUNDER) {
	// 	promises.push(getAllWithAnswers({ category: QUESTION_CATEGORIES.TEAM_MEMBER, per_page: 100 }));
	// }
	const promise = Promise.all(promises).then(([questions]) => {
		// let questions = [];
		// const profileFieldKeys = profileQuestions.reduce((acc, q) => {
		// 	if (q.meta.field) {
		// 		questions.push(q);
		// 		const field = q.meta.field.split(".");
		// 		acc.push(field[field.length - 1]);
		// 	}
		// 	return acc;
		// }, []);
		// if (teamQuestions) {
		// 	questions = questions.concat(
		// 		teamQuestions.reduce((acc, question) => {
		// 			const fieldPath = question.meta.field?.split(".") || [];
		// 			const key = fieldPath[fieldPath.length - 1];
		// 			if (
		// 				!question.meta.field ||
		// 				question.meta.field === "fullyQualifiedPersonName" ||
		// 				(key && profileFieldKeys.includes(key))
		// 			) {
		// 				return acc;
		// 			}
		// 			question.meta.step = 2;
		// 			question.group = "Professional Information";
		// 			question.meta.field = "teamMember." + question.meta.field;
		// 			acc.push(question);
		// 			return acc;
		// 		}, [])
		// 	);
		// }
		return {
			questions,
		};
	});
	return defer({ promise });
}
